import React, { useEffect, useState } from "react";
import { CircularProgress, IconButton, MenuItem, Select, TextField, Typography } from "@mui/material";
import { Loop } from "@mui/icons-material";
import { t } from "i18next";

import "./style.scss";

interface IBaseSelectAsyncProps<T extends {}> {
    readOnly?: boolean;
    isLoading: boolean;
    loadingLabel?: string;
    options: T[];
    getOptionLabel: (option: T) => string;
    getOptionValue: (option: T) => string;
    isEqual?: (option1: T, option2: T) => boolean;
    value: T | undefined;
    onValueChange: (value: T | undefined) => void;
}

interface ISelectAsyncWithoutCustomValueProps<T extends {}> extends IBaseSelectAsyncProps<T> {
    withCustomValue?: false;
}

interface ISelectAsyncWithCustomValueProps<T extends {}> extends IBaseSelectAsyncProps<T> {
    withCustomValue: true;
    customValueOptionLabel?: string;
    onCustomValueSelected: (isSelected: boolean) => void;
    buildCustomValue?: (value: string) => T;
}

type ISelectAsyncProps<T extends {}> = ISelectAsyncWithoutCustomValueProps<T> | ISelectAsyncWithCustomValueProps<T>;

export const SelectAsync = <T extends {}>(props: ISelectAsyncProps<T>) => {
    const [isCustomValueMode, setIsCustomValueMode] = useState(false);
    const [customValueModeTextInputAutoFocus, setCustomValueModeTextInputAutoFocus] = useState<boolean>(false);
    const [selectValue, setSelectValue] = useState(props.isLoading ? props.value : undefined);
    const [textValue, setTextValue] = useState(props.value ? props.getOptionLabel(props.value) : "");

    const buildCustomValue = (name: string) =>
        props.withCustomValue && props.buildCustomValue ?
            props.buildCustomValue(name) : selectValue as T;
    const isOptionValueEqual = (o1: T, o2: T) =>
        props.isEqual ? props.isEqual(o1, o2) : props.getOptionValue(o1) === props.getOptionValue(o2);

    const handleSelectValueChanged = (value: string) => {
        setSelectValue(props.options.find(o => props.getOptionValue(o) === value) ?? undefined);
    };

    const handleCustomValueChanged = (value: string) => {
        if (!props.withCustomValue) {
            return;
        }

        setTextValue(value);
        props.onValueChange(buildCustomValue(value));
    };

    const handleCustomValueModeEnable = () => {
        if (!props.withCustomValue) {
            return;
        }

        setIsCustomValueMode(true);
        props.onCustomValueSelected(true);
        setCustomValueModeTextInputAutoFocus(true);
    };

    const handleSelectOptionModeEnable = () => {
        if (!props.withCustomValue) {
            return;
        }

        setCustomValueModeTextInputAutoFocus(false);
        setIsCustomValueMode(false);
        props.onCustomValueSelected(false);
    };

    useEffect(() => {
        if (props.withCustomValue && isCustomValueMode) {
            props.onValueChange(buildCustomValue(textValue));
        } else {
            props.onValueChange(selectValue);
        }
    }, [selectValue, textValue]);

    useEffect(() => {
        if (props.isLoading || props.options.length === 0 || !props.value) {
            return;
        }

        if (isCustomValueMode && props.getOptionLabel(props.value) === textValue) {
            return;
        }

        const selectedOption = props.options.find(option => isOptionValueEqual(option, props.value!));
        setSelectValue(selectedOption);
        if (!selectedOption && props.withCustomValue) {
            setTextValue(props.getOptionLabel(props.value));
            setIsCustomValueMode(true);
            props.onCustomValueSelected(true);
        }
    }, [props.isLoading, props.options, props.value]);

    if (isCustomValueMode) {
        return <TextField value={textValue} autoFocus={customValueModeTextInputAutoFocus}
                          slotProps={{ input: {
                              readOnly: props.readOnly,
                              endAdornment: props.readOnly ?
                                  undefined :
                                  <IconButton onClick={handleSelectOptionModeEnable}>
                                      <Loop />
                                  </IconButton> } }}
                          onChange={e => handleCustomValueChanged(e.target.value)} />;
    }

    return <Select displayEmpty readOnly={props.readOnly}
                   value={!props.isLoading && props.value ? props.getOptionValue(props.value) : ""}
                   className={"custom-select"}
                   disabled={props.isLoading}
                   renderValue={option => option || (props.isLoading ?
                       <Typography color={"textSecondary"} className={"custom-select__option custom-select__loading"}>
                           <CircularProgress size={"14px"} color={"inherit"} />
                           <span>{props.loadingLabel ?? t("components.selectAsync.status.loading")}</span>
                       </Typography> :
                       <Typography className={"custom-select__option"} color={"textSecondary"}>
                           {t("components.selectAsync.selectOption")}
                       </Typography>)}
                   onChange={e => handleSelectValueChanged(e.target.value as string)}>
        {props.options.map(option =>
            <MenuItem key={`option-${props.getOptionValue(option)}`} value={props.getOptionValue(option)}>
                {props.getOptionLabel(option)}
            </MenuItem>)}
        {props.withCustomValue &&
            <MenuItem onClick={handleCustomValueModeEnable}>
                {props.customValueOptionLabel ?? t("components.selectAsync.customOptionLabel")}
            </MenuItem>}
    </Select>;
};
